import {
    Component,
    OnInit,
    OnDestroy,
    Input,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { SharedModule } from '../../shared.module';
import { Observable, Subject, Subscription, switchMap } from 'rxjs';
import {
    ChartType,
    GraphDataItem,
    SelectedParams,
    ValueBridgeGraphData
} from '../../state/value-bridge/sb-value-bridge.model';
import { SbEventBusService, SbEventTopic } from '@services';
import { debounceTime, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { TablerIconsModule } from 'angular-tabler-icons';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { Currency } from '../currency-selector/currency-country-codes';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import {
    SbKeyMetrics,
    SbKeyMetricsComponent
} from '../sb-key-metrics/sb-key-metrics.component';
import { combineQueries } from '@datorama/akita';
import { TestSelectorDirective } from '../../directives/test-selector.directive';
import { fadeInOut, list } from '../../animations/fade-in-out.animation';
import { SbValueBridgeStore } from '../../state/value-bridge-legacy/sb-value-bridge.store';
import { SbValueBridgeQuery } from '../../state/value-bridge-legacy/sb-value-bridge.query';
import { SbValueBridgeService } from '../../state/value-bridge-legacy/sb-value-bridge.service';

const START_OF_CURRENT_YEAR = new Date(new Date().getFullYear(), 0, 1);
const DEBOUNCE_TIME_MS = 1000;
const MAX_BAR_HEIGHT_FACTOR = 0.95; //to leave a margin between the container and the bar
const DATE_FORMAT = 'YYYY-MM-DD';

/** @deprecated Marking this component as deprecated since it contains a lot of anti-patterns, broken streams and things that need to be refactored
 * TODO: re-write component */
@Component({
    selector: 'sb-value-bridge-legacy',
    templateUrl: './sb-value-bridge-legacy.component.html',
    styleUrls: ['./sb-value-bridge-legacy.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        SharedModule,
        TablerIconsModule,
        MatMenuModule,
        MatTooltipModule,
        SbKeyMetricsComponent,
        TestSelectorDirective
    ],
    providers: [DatePipe],
    animations: [fadeInOut, list]
})
export class SbValueBridgeLegacyComponent implements OnInit, OnDestroy {
    @Input() containerHeight = 250;
    @Input() chartWidth = 1420;
    @Input() isChild: boolean = false;
    @Input() title = 'Value Bridge';

    private _isChild$ = new BehaviorSubject(false);

    public graphData$: Observable<ValueBridgeGraphData | null>;
    public marginBottoms: number[] = []; // To store cumulative heights of bars
    public maxValue: number; // Find the max absolute value for scaling
    public description = 'View your value changes over time.';
    public form: FormGroup;

    public barStyles: any[] = [];
    public labelTopStyles: string[] = [];
    public lineProperties: any[] = [];
    public showChildComponent: boolean = false;

    // Set default values for fromDateCtrl and toDateCtrl
    public fromDateCtrl = new FormControl<Date>(START_OF_CURRENT_YEAR);
    public toDateCtrl = new FormControl<Date>(new Date());
    public types: Array<ChartType> = [
        { label: 'Account', value: 'portfolio' },
        {
            label: 'Instrument Type',
            value: 'asset-type'
        }
    ];
    public typeCtrl = new FormControl<ChartType>(this.types[0]);

    private loadingBehaviour$ = new BehaviorSubject(true);
    public loading$ = this.loadingBehaviour$.asObservable().pipe(shareReplay(1));

    private subscription: Subscription = new Subscription();
    private graphDataLength: number;
    private cachedGraphData: ValueBridgeGraphData;
    private baseCurrency$ = this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY');
    //region selected values

    @Input() selectedParams: SelectedParams = {
        base_currency: 'SEK',
        start_date: this.datePipe.transform(this.fromDateCtrl.value, 'yyyy-MM-dd'),
        end_date: this.datePipe.transform(this.toDateCtrl.value, 'yyyy-MM-dd'),
        account_ids: [],
        group: '',
        chartTypeValue: 'portfolio',
        pretty_name: ''
    };

    //endregion
    private unsubscribe$: Subject<void> = new Subject();
    useShortNumberFormat: any;

    constructor(
        private valueBridgeService: SbValueBridgeService,
        private valueBridgeQuery: SbValueBridgeQuery,
        private valueBridgeStore: SbValueBridgeStore,
        private sbEventBusService: SbEventBusService,
        private _userSettingsQuery: UserSettingsQuery,
        private formBuilder: FormBuilder,
        private datePipe: DatePipe
    ) {}

    public get barWidthPx() {
        return this.barWidth.toString() + 'px';
    }

    private get barWidth() {
        const baseLength = 2;
        const stepValue = 0.35;
        const adjuster = (this.graphDataLength - baseLength) * stepValue - stepValue;

        const divider = this.graphDataLength * 2 + adjuster;

        return this.chartWidth / divider;
    }

    private get maxBarHeight() {
        return this.containerHeight * MAX_BAR_HEIGHT_FACTOR;
    }

    ngOnInit() {
        this.form = this.formBuilder.group({
            typeCtrl: [this.types[0].value],
            fromDate: [moment(START_OF_CURRENT_YEAR)],
            toDate: [moment()] // Default to current date
        });

        this.selectGraphData();

        this.observeGraphData();

        this.observeAccountSelection();

        this.handleFormChanges();

        this.observeBaseCurrencyChanges();

        if (
            this.selectedParams.account_ids &&
            this.selectedParams.account_ids.length > 0
        ) {
            this.fetchValueBridgeData(this.selectedParams);
        }

        this.description = this.isChild
            ? 'NAV change for your selected portfolio.'
            : this.description;
    }

    private selectGraphData() {
        if (!this.isChild) {
            this.graphData$ = this.valueBridgeQuery.selectParentGraphData();
        } else {
            this.graphData$ = this.valueBridgeQuery.selectChildGraphData();
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public getBottomLineWidth() {
        const chartWidth =
            this.graphDataLength * (this.barWidth * 2) - this.barWidth + 'px';
        return chartWidth;
    }

    public getLineProperties(leftBar: GraphDataItem, rightBar: GraphDataItem, i: number) {
        return SbValueBridgeService.getLineProperties(
            leftBar,
            rightBar,
            i,
            this.maxValue,
            this.maxBarHeight,
            this.barWidth,
            this.cachedGraphData
        );
    }

    public getHeightOfBar(value: number) {
        return SbValueBridgeService.calculateHeightOfBar(
            value,
            this.maxValue,
            this.maxBarHeight
        );
    }

    public getMarginBottomOfBar(i: number) {
        return SbValueBridgeService.calculateMarginBottomOfBar(
            i,
            this.maxValue,
            this.maxBarHeight,
            this.marginBottoms
        );
    }

    public getLeftPositionOfBar(i: number) {
        return SbValueBridgeService.calculateLeftPositionOfBar(i, this.barWidth);
    }

    private handleFormChanges() {
        this.form.valueChanges
            .pipe(debounceTime(DEBOUNCE_TIME_MS), takeUntil(this.unsubscribe$))
            .subscribe((values) => {
                if (values) {
                    this.applySelectedParamsAndFetchData(values);
                }
            });
    }

    private applySelectedParamsAndFetchData(values: { typeCtrl; fromDate; toDate }) {
        const { typeCtrl, fromDate, toDate } = values;

        this.selectedParams = {
            ...this.selectedParams,
            chartTypeValue: typeCtrl,
            start_date: fromDate.format(DATE_FORMAT),
            end_date: toDate.format(DATE_FORMAT)
        };

        if (
            this.selectedParams.account_ids &&
            this.selectedParams.account_ids.length > 0
        ) {
            this.fetchValueBridgeData(this.selectedParams);
        }
    }

    private fetchValueBridgeData(params: SelectedParams) {
        this.updateGraphData(null);

        this.loadingBehaviour$.next(true);
        return this.valueBridgeService
            .getValueBridgeData(
                { ...params, chartType: params.chartTypeValue },
                params.group
            )
            .pipe(take(1))
            .subscribe({
                next: (graphData) => {
                    if (graphData && Array.isArray(graphData.graph)) {
                        this.filterValueWithZero(graphData);

                        this.updateGraphData(graphData);

                        this.prepareStyles(graphData);
                        this.loadingBehaviour$.next(false);
                    }
                },
                error: (error) => {
                    console.error('Error fetching asset type data:', error);
                }
            });
    }

    private filterValueWithZero(graphData: ValueBridgeGraphData | null) {
        if (graphData && graphData.graph && Array.isArray(graphData.graph)) {
            graphData.graph = graphData.graph.filter(
                (g) =>
                    g.ordinal === 0 ||
                    g.ordinal === graphData.graph.length - 1 ||
                    g.value !== 0
            );
        } else {
            console.warn(
                'graphData or graph is null/undefined or not an array:',
                graphData
            );
        }
    }

    private updateGraphData(graphData: ValueBridgeGraphData | null) {
        if (!this.isChild) {
            this.valueBridgeStore.updateParentGraphData(graphData);
        } else {
            this.valueBridgeStore.updateChildGraphData(graphData);
        }
    }

    private observeAccountSelection() {
        this.subscription.add(
            this.sbEventBusService
                .getObservable(SbEventTopic.AccountSelected)
                .pipe(debounceTime(DEBOUNCE_TIME_MS))
                .subscribe(
                    (event) => {
                        if (event && event.payload && event.payload.value) {
                            this.selectedParams.account_ids = event.payload.value;
                            if (
                                this.selectedParams.account_ids &&
                                this.selectedParams.account_ids.length > 0
                            ) {
                                this.fetchValueBridgeData({
                                    ...this.selectedParams,
                                    account_ids: this.selectedParams.account_ids
                                });
                            }
                        }
                    },
                    (error) => console.error(error)
                )
        );
    }

    private observeBaseCurrencyChanges() {
        this.subscription.add(
            this.baseCurrency$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((currency: Currency) => {
                    this.selectedParams.base_currency = currency;
                    this.fetchValueBridgeData(this.selectedParams);
                })
        );
    }

    private observeGraphData() {
        this.subscription.add(
            this.graphData$.subscribe((graphData) => {
                if (graphData && graphData.graph) {
                    this.marginBottoms =
                        SbValueBridgeService.calculateMarginBottoms(graphData);

                    this.cachedGraphData = graphData;

                    // console.log({ graphData });

                    this.graphDataLength = graphData.graph.length;

                    // Update maxValue based on the new graphData
                    this.maxValue = Math.max(
                        ...graphData.graph.map((item) => Math.abs(item.value))
                    );
                }
            })
        );
    }

    private prepareStyles(graphData: ValueBridgeGraphData) {
        if (graphData && graphData.graph) {
            this.barStyles = graphData.graph.map((item, index) => ({
                height: this.getHeightOfBar(item.value) + 'px',
                marginBottom: this.getMarginBottomOfBar(index) + 'px',
                left: this.getLeftPositionOfBar(index) + 'px',
                width: this.barWidthPx
            }));

            this.labelTopStyles = graphData.graph.map(
                (item, index) =>
                    `${
                        this.getMarginBottomOfBar(index) +
                        this.getHeightOfBar(item.value) -
                        3
                    }px`
            );

            this.lineProperties = graphData.graph.map((item, index) =>
                this.getLineProperties(item, graphData.graph[index + 1], index)
            );
        }
    }

    onBarClick(group: string) {
        this.showChildComponent = true;
        this.selectedParams.group = group;
        this.selectedParams.pretty_name = this.cachedGraphData.graph.find(
            (g) => g.group === group
        ).pretty_name;
    }

    onOverlayClick() {
        this.showChildComponent = false;
        this.selectedParams.group = undefined;
    }

    toggleShortNumberFormat() {}

    metrics$: Observable<SbKeyMetrics> = combineQueries([
        this._isChild$.pipe(switchMap((_) => this.graphData$)),
        this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY')
    ]).pipe(
        map(([graphData, ccy]) => {
            return [
                {
                    title: 'Value change',
                    value: graphData?.totals?.absolute_change ?? null,
                    change: graphData?.totals?.relative_change ?? null,
                    prefixPositiveValue: true,
                    addColor: true,
                    ccy,
                    formatStyle: 'short',
                    description:
                        'Absolute and relative value change during selected period.'
                }
            ];
        })
    );
}
